import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ShoppingCartSimple, ArrowSquareOut, Star, Wrench, CaretRight } from '@phosphor-icons/react';
import { setPageMeta } from '../utils/seo';

const API = process.env.REACT_APP_BACKEND_URL;

export const openAmazon = async (productId) => {
  try {
    const r = await axios.post(`${API}/api/store/click/${productId}`);
    const tg = window.Telegram?.WebApp;
    if (tg?.initData && typeof tg.openLink === 'function') tg.openLink(r.data.url);
    else window.open(r.data.url, '_blank', 'noopener');
  } catch (e) { /* ignore */ }
};

const ProductCard = ({ p, idx }) => (
  <button onClick={() => openAmazon(p.id)}
    className="group bg-white dark:bg-neutral-800 border-2 border-black rounded-xl overflow-hidden shadow-[4px_4px_0px_#000] hover:shadow-[4px_4px_0px_#D95800] hover:border-[#D95800] hover:-translate-y-1 transition-[transform,box-shadow,border-color] duration-200 text-left flex flex-col w-full animate-in fade-in slide-in-from-bottom-2"
    style={{ animationDelay: `${Math.min(idx * 40, 400)}ms`, animationFillMode: 'backwards' }}
    data-testid={`store-product-${p.id}`}>
    <div className="aspect-square bg-white flex items-center justify-center p-4 overflow-hidden relative">
      {p.featured && (
        <span className="absolute top-2 left-2 z-10 inline-flex items-center gap-1 text-[9px] font-black uppercase bg-[#D95800] text-white px-2 py-0.5 border border-black rounded-full shadow-[2px_2px_0px_#000]">
          <Star size={10} weight="fill" /> Pro Pick
        </span>
      )}
      <img src={p.image_url} alt={p.title} loading="lazy"
        className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-300"
        onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/store-placeholder.jpg'; }} />
    </div>
    <div className="p-3 border-t-2 border-black flex flex-col flex-1">
      <span className="text-[9px] font-black uppercase tracking-wider text-neutral-400">{p.category_label}</span>
      <p className="font-bold text-sm text-neutral-900 dark:text-white mt-1 leading-snug line-clamp-2">{p.title}</p>
      {p.description && <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1 leading-snug line-clamp-2">{p.description}</p>}
      <div className="flex items-center justify-between mt-auto pt-3">
        <span className="font-black text-lg text-[#D95800]">{p.price_display}</span>
        <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase px-2.5 py-1.5 rounded-full border-2 border-black bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-200 group-hover:bg-[#D95800] group-hover:text-white group-hover:border-[#D95800] transition-colors">
          Amazon <ArrowSquareOut size={12} weight="bold" />
        </span>
      </div>
    </div>
  </button>
);

const FeaturedSpotlight = ({ products }) => (
  <div className="grid md:grid-cols-2 gap-4 mb-10" data-testid="store-featured">
    {products.map((p, i) => (
      <button key={p.id} onClick={() => openAmazon(p.id)}
        className="group bg-[#0F172A] border-2 border-black rounded-2xl overflow-hidden shadow-[6px_6px_0px_#D95800] hover:-translate-y-1 transition-transform text-left flex animate-in fade-in slide-in-from-bottom-3"
        style={{ animationDelay: `${i * 100}ms`, animationFillMode: 'backwards' }}
        data-testid={`store-featured-${p.id}`}>
        <div className="w-2/5 bg-white flex items-center justify-center p-4 shrink-0">
          <img src={p.image_url} alt={p.title} loading="lazy"
            className="max-h-40 max-w-full object-contain group-hover:scale-105 transition-transform duration-300"
            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/store-placeholder.jpg'; }} />
        </div>
        <div className="p-4 md:p-5 flex flex-col justify-center">
          <span className="inline-flex items-center gap-1 self-start text-[9px] font-black uppercase bg-[#D95800] text-white px-2 py-0.5 rounded-full">
            <Star size={10} weight="fill" /> Editor's Pick
          </span>
          <p className="font-black text-white text-base md:text-lg leading-snug mt-2 line-clamp-2">{p.title}</p>
          {p.description && <p className="text-xs text-neutral-400 mt-1 line-clamp-2">{p.description}</p>}
          <div className="flex items-center gap-3 mt-3">
            <span className="font-black text-xl text-[#D95800]">{p.price_display}</span>
            <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase px-3 py-1.5 rounded-full bg-[#D95800] text-white group-hover:bg-white group-hover:text-black transition-colors">
              Shop Now <ArrowSquareOut size={12} weight="bold" />
            </span>
          </div>
        </div>
      </button>
    ))}
  </div>
);

export default function Store() {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get('category') || '';
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/store/products${category ? `?category=${category}` : ''}`)
      .then((r) => setData(r.data)).catch(() => setData({ products: [], categories: [] }));
    setPageMeta({
      title: 'Recommended Tools & Gear | ConstructHelp Store',
      description: 'Hand-picked tools, safety gear and supplies our pros actually use — with direct links to buy on Amazon.',
      url: `${window.location.origin}/store`,
    });
    window.scrollTo(0, 0);
  }, [category]);

  const products = data?.products || [];
  const featured = !category ? products.filter((p) => p.featured).slice(0, 2) : [];
  const categories = data?.categories || [];
  const byCategory = categories
    .map((c) => ({ ...c, items: products.filter((p) => p.category === c.id) }))
    .filter((c) => c.items.length > 0);

  return (
    <div className="min-h-screen bg-[#E8E9EB] dark:bg-[#121212]" data-testid="store-page">
      <div className="bg-[#0F172A] border-b-4 border-[#D95800]">
        <div className="max-w-5xl mx-auto px-4 py-12">
          <div className="flex items-center gap-2 mb-3">
            <ShoppingCartSimple size={20} weight="bold" className="text-[#D95800]" />
            <span className="text-[10px] font-black uppercase tracking-widest text-[#D95800]">The Tool Store</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-black uppercase tracking-tight text-white leading-tight">
            Recommended<br />Tools & Gear
          </h1>
          <p className="text-neutral-400 mt-3 max-w-xl text-sm md:text-base">
            Hand-picked gear our pros actually use. Buying used instead? Check the <a href="/marketplace" className="text-[#D95800] underline">Marketplace</a>.
          </p>
          <div className="inline-flex items-center gap-2 mt-4 bg-white/5 border border-white/10 rounded-full px-3 py-1.5" data-testid="store-trust-strip">
            <Wrench size={14} weight="bold" className="text-[#D95800]" />
            <span className="text-[11px] font-bold text-neutral-300">Every pick recommended by contractors on ConstructHelp</span>
          </div>
        </div>
      </div>

      <div className="sticky top-0 z-30 bg-[#E8E9EB]/90 dark:bg-[#121212]/90 backdrop-blur-md border-b border-neutral-300 dark:border-neutral-800">
        <div className="max-w-5xl mx-auto px-4 py-3 flex flex-nowrap gap-2 overflow-x-auto [-webkit-overflow-scrolling:touch]" data-testid="store-category-filter">
          <button onClick={() => setSearchParams({})}
            className={`text-xs font-black uppercase px-3 py-1.5 border-2 border-black rounded-full whitespace-nowrap transition-colors ${!category ? 'bg-black text-white' : 'bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:border-[#D95800]'}`}
            data-testid="store-filter-all">All</button>
          {categories.map((c) => (
            <button key={c.id} onClick={() => setSearchParams({ category: c.id })}
              className={`text-xs font-black uppercase px-3 py-1.5 border-2 border-black rounded-full whitespace-nowrap transition-colors ${category === c.id ? 'bg-black text-white' : 'bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:border-[#D95800]'}`}
              data-testid={`store-filter-${c.id}`}>{c.label}</button>
          ))}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {data && products.length === 0 ? (
          <div className="text-center py-20" data-testid="store-empty">
            <ShoppingCartSimple size={56} weight="duotone" className="text-neutral-300 mx-auto mb-4" />
            <p className="font-black uppercase text-neutral-700 dark:text-neutral-300">Stocking the shelves…</p>
            <p className="text-sm text-neutral-500 mt-1">Our first picks are on the way.</p>
          </div>
        ) : category ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="store-grid">
            {products.map((p, i) => <ProductCard key={p.id} p={p} idx={i} />)}
          </div>
        ) : (
          <>
            {featured.length > 0 && <FeaturedSpotlight products={featured} />}
            {byCategory.map((c) => (
              <div key={c.id} className="mb-10" data-testid={`store-section-${c.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-black uppercase tracking-tight text-neutral-900 dark:text-white">{c.label}</h2>
                  <button onClick={() => setSearchParams({ category: c.id })}
                    className="inline-flex items-center gap-0.5 text-[11px] font-black uppercase text-[#D95800] hover:underline"
                    data-testid={`store-see-all-${c.id}`}>
                    See all {c.items.length} <CaretRight size={12} weight="bold" />
                  </button>
                </div>
                <div className="flex gap-4 overflow-x-auto pb-3 snap-x [-webkit-overflow-scrolling:touch]" data-testid={`store-row-${c.id}`}>
                  {c.items.map((p, i) => (
                    <div key={p.id} className="min-w-[190px] w-[190px] md:min-w-[230px] md:w-[230px] snap-start shrink-0 flex">
                      <ProductCard p={p} idx={i} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </>
        )}

        <p className="text-[11px] text-neutral-400 text-center mt-10 max-w-2xl mx-auto" data-testid="affiliate-disclosure">
          As an Amazon Associate, ConstructHelp earns from qualifying purchases. Prices shown are indicative — the current price is always displayed on Amazon.
        </p>
      </div>
    </div>
  );
}
